import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import {
  Button,
  ButtonBlock,
  List,
  PageHeader,
  Tab,
  useToast,
} from '@politechdev/blocks-design-system'
import { Paginator, View, ViewContainer } from 'components'
import { useCurrent } from 'contexts'
import { useRequest } from 'hooks'
import { getPerformanceReviewList } from './utils'
import {
  PerformanceReviewFilters,
  PerformanceReviewItem,
  TabType,
} from './types'
import Filters from './PerformanceReviews/Filters'
import ReviewItem from './PerformanceReviews/ReviewItem'
import { TABS } from './constants'

const PerformanceReviews = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { doesCurrentUserHavePermission } = useCurrent()
  const { setToast } = useToast()

  const [currentFilters, setCurrentFilters] =
    useState<PerformanceReviewFilters>({})

  const [selectedTab, setSelectedTab] = useState<TabType>(TABS.ACTIVE)

  const canModify = doesCurrentUserHavePermission({
    resource: 'performance_review',
    ability: 'modify',
  })

  const getPerformanceReviewListRequest = useRequest(getPerformanceReviewList, {
    onError: () => {
      setToast({
        message: t('An issue occured while fetching performance reviews'),
        variant: 'error',
      })
    },
  })

  useEffect(() => {
    void getPerformanceReviewListRequest.makeRequest({
      page: 1,
      tab: selectedTab,
      filters: currentFilters,
    })
  }, [])

  const performanceReviews =
    getPerformanceReviewListRequest.response?.performance_reviews ?? []
  const currentPage =
    getPerformanceReviewListRequest.response?.meta.current_page ?? 1
  const totalCount =
    getPerformanceReviewListRequest.response?.meta.total_count ?? 0
  const per = getPerformanceReviewListRequest.response?.meta.per ?? 10
  const totalPages = Math.ceil(totalCount / per) || 1

  const handlePageNavigation = (
    page: number,
    tab: TabType = selectedTab,
    filters: PerformanceReviewFilters = currentFilters
  ) => {
    void getPerformanceReviewListRequest.makeRequest({
      page,
      tab,
      filters,
    })
  }

  const handleTabChange = (tabId: TabType) => {
    setSelectedTab(tabId)
    setCurrentFilters({})
    handlePageNavigation(1, tabId, {})
  }

  const updateFilters = (updates: Partial<PerformanceReviewFilters>) => {
    setCurrentFilters({ ...currentFilters, ...updates })
    handlePageNavigation(1, undefined, { ...currentFilters, ...updates })
  }

  return (
    <View>
      <PageHeader title={t('Performance reviews')}>
        <ButtonBlock justify="right">
          <Link
            onClick={e => !canModify && e.preventDefault()}
            to={`${location.pathname}/new`}
          >
            <Button.Accent disabled={!canModify}>{t('Create')}</Button.Accent>
          </Link>
        </ButtonBlock>
      </PageHeader>
      <ViewContainer loading={getPerformanceReviewListRequest.isLoading}>
        <Tab.Container
          selectedTabId={selectedTab}
          onChange={({ tabId }) => handleTabChange(tabId as TabType)}
        >
          <Tab id={TABS.ACTIVE} label={t('Active')}>
            <Filters
              filters={currentFilters}
              updateFilters={updateFilters}
              activeTab={selectedTab}
            />
            <List
              itemData={performanceReviews}
              loading={getPerformanceReviewListRequest.isLoading}
              emptyMessage={t('No performance reviews found')}
              render={item => {
                const review = item as PerformanceReviewItem

                return <ReviewItem review={review} />
              }}
            />
            <Paginator
              currentPage={currentPage}
              totalPages={totalPages}
              onNext={() => handlePageNavigation(currentPage + 1)}
              onPrevious={() => handlePageNavigation(currentPage - 1)}
              justify="left"
            />
          </Tab>
          <Tab id={TABS.COMPLETED} label={t('Completed')}>
            <Filters
              filters={currentFilters}
              updateFilters={updateFilters}
              activeTab={selectedTab}
            />
            <List
              itemData={performanceReviews}
              loading={getPerformanceReviewListRequest.isLoading}
              emptyMessage={t('No performance reviews found')}
              render={item => {
                const review = item as PerformanceReviewItem

                return <ReviewItem review={review} />
              }}
            />
            <Paginator
              currentPage={currentPage}
              totalPages={totalPages}
              onNext={() => handlePageNavigation(currentPage + 1)}
              onPrevious={() => handlePageNavigation(currentPage - 1)}
              justify="left"
            />
          </Tab>
        </Tab.Container>
      </ViewContainer>
    </View>
  )
}

export default PerformanceReviews
